import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import * as composeActions from "@bca-compose/store/compose.actions";
import { ProductIdentifier } from "@bca-shared/models/_enums";
import { AddonId } from "@bca-shared/models/_types";
import { IAddon } from "@bca-shared/models/addon";
import { IProduct } from "@bca-shared/models/product";
import { AvoEventTypes } from "@bca-shared/utils/avo/avo-event-details.models";
import { AvoService } from "@bca-shared/utils/avo/avo.service";
import * as appActions from "@bca-store/app.actions";
import { IAction } from "@bca-store/models/action";
import { createAction } from "@bca-store/models/action-creator";
import { IRootState } from "@bca-store/store.reducers";
import { selectTotalProductCount } from "@bca-store/store.selectors";
import { environment } from "@environments/environment";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { combineLatest, filter, map, take } from "rxjs/operators";

@Component({
  selector: "bca-choose-extras",
  templateUrl: "choose-extras.component.html",
  styleUrls: ["choose-extras.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChooseExtrasComponent implements OnInit {
  @ViewChild("useExtrasButtonTrue") useExtrasButtonTrue: ElementRef;
  @ViewChild("useExtrasButtonFalse") useExtrasButtonFalse: ElementRef;
  @Input()
  selectedProduct: IProduct;
  @Output()
  addProduct = new EventEmitter<void>();

  hasUnselectedServices$ = this._store$.pipe(
    select(({ compose }) => compose.selectedProduct),
    filter((selectedProduct) => selectedProduct != null),
    map((selectedProduct) =>
      selectedProduct.services.some((service: IAddon) => !service.selected),
    ),
  );
  servicesNotExpanded$: Observable<boolean>;
  showProductInfo$ = this._store$.pipe(
    select(selectTotalProductCount),
    map((count) => count === 1),
  );
  routeLoading$ = this._store$.pipe(
    select(({ compose }) => compose.routeLoading),
    combineLatest(this._store$.pipe(select(({ app }) => app.dialogStatus))),
    map(
      ([routeLoading, dialogStatus]) =>
        routeLoading && !dialogStatus[ProductIdentifier.Traject],
    ),
  );
  showPhoneNumber$ = this._store$.pipe(select(({ app }) => app.showPhone));
  maxBuyableProducts = environment.receipt.maxBuyableProducts;
  ProductIdentifier = ProductIdentifier;

  constructor(
    private _store$: Store<IRootState>,
    private _avoService: AvoService,
    private _translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.servicesNotExpanded$ = this._store$.pipe(
      select(({ compose }) => compose.serviceTileToggleState),
      map(
        (serviceTileToggleState) =>
          !!serviceTileToggleState[this.selectedProduct.id],
      ),
    );
  }
  private getSelectedServices$() {
    return this._store$.pipe(
      select(({ compose }) => compose.selectedProduct),
      map((selectedProduct) =>
        selectedProduct ? selectedProduct.services : [],
      ),
    );
  }

  allServicesSelected$ = this.getSelectedServices$().pipe(
    map((services) => {
      if (!services || services.length === 0) {
        return true;
      }
      return services.every((service) => service.selected);
    }),
  );

  someServicesSelected$ = this.getSelectedServices$().pipe(
    map((services) => {
      if (!services || services.length === 0) {
        return false;
      }

      const selectedServicesCount = services.filter(
        (service) => service.selected,
      ).length;
      return (
        selectedServicesCount > 0 && selectedServicesCount < services.length
      );
    }),
  );

  noServicesSelected$ = this.getSelectedServices$().pipe(
    map((services) => {
      if (!services || services.length === 0) {
        return true;
      }
      return services.every((service) => !service.selected);
    }),
  );

  onAddonSelected(addon: IAddon, selected: boolean): void {
    this._store$.dispatch(
      this._createAction(new composeActions.SelectAddon({ addon, selected })),
    );
    this.handleAvoEvents("selectable_click", addon, selected);
  }

  onServiceSelected(service: IAddon, selected: boolean): void {
    this._store$.dispatch(
      this._createAction(
        new composeActions.SelectService({ service, selected }),
      ),
    );
    this.handleAvoEvents("toggle_click", service, selected);
  }

  toggleServicesExpandedOld(checked): void {
    // prevent selectable losing its border on a second click untill fix nessie
    checked
      ? (this.useExtrasButtonTrue.nativeElement.checked = true)
      : (this.useExtrasButtonFalse.nativeElement.checked = true);

    this.servicesNotExpanded$
      .pipe(
        take(1),
        map((expanded) => !expanded),
      )
      .subscribe((expanded) => {
        this._store$.dispatch(
          this._createAction(
            new composeActions.SetServiceTileToggleState({
              productId: String(this.selectedProduct.id),
              expanded,
            }),
          ),
        );
      });
  }

  toggleServicesExpanded(): void {
    this.servicesNotExpanded$
      .pipe(
        take(1),
        map((expanded) => !expanded),
      )
      .subscribe((expanded) => {
        this._store$.dispatch(
          this._createAction(
            new composeActions.SetServiceTileToggleState({
              productId: String(this.selectedProduct.id),
              expanded,
            }),
          ),
        );
      });
  }

  onActionClicked(action: appActions.Action): void {
    this._store$.dispatch(this._createAction(action));
  }

  trackByIdentifier(i: number, service: IAddon): AddonId {
    return service.identifier;
  }

  private _createAction(action: IAction): IAction {
    return createAction(action, ChooseExtrasComponent);
  }

  private handleAvoEvents(
    eventType: AvoEventTypes,
    addon: IAddon,
    selected: boolean,
  ): void {
    switch (eventType) {
      case "toggle_click":
        const addOnName = this._translate.instant(
          "COMPOSE.CHOOSE_EXTRAS.SERVICES." + addon.identifier + ".TITLE",
        );
        const unigue_name = addOnName + " set to " + selected;
        this._avoService.toggleClick({
          clickText: addOnName,
          uniqueName: unigue_name,
          description: "selecting a dtd service",
          checked: selected,
          websiteSection: "ns-zakelijk|checkout|samenstellen",
        });
        break;
      case "selectable_click":
        let buttonText = addon.identifier.includes("UPDATE")
          ? selected
            ? "1e klasse"
            : "2e klasse"
          : selected
            ? "Ja"
            : "Nee";

        let data = addon.identifier.includes("UPDATE")
          ? "standaard 1e of 2e klasse reizen"
          : "Standaard Intercity direct Altijd Toeslagvrij";

        let endtext = selected ? ": true" : ": false";

        this._avoService.selectableClick({
          uniqueName: data + " : " + buttonText,
          description: data,
          websiteSection: "ns-zakelijk|checkout|samenstellen",
          clickText: buttonText,
          optionList: null,
          selectedOption: buttonText,
          optionAmount: null,
        });

        break;
      default:
        console.warn(`Unhandled event type: ${eventType}`);
    }
  }
}
